import React, { useContext } from "react";
import { Link, Location } from "react-router-dom";
import { useDispatch } from "react-redux";
import Parser from "html-react-parser";
import { get } from "lodash";

import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import { CardSlot } from "@fdr-mar/promos-types/Component";
import { LinkType } from "@fdr-mar/promos-types/PathHandling";
// @ts-ignore
import Context from "@fdr-mar/promos-context";
import { Tag, Icon, Paragraph } from "@tvg/design-system/web";
import { onbackPromosModal } from "@urp/promos-page/redux/actions";
import { isTvg5 } from "@tvg/utils/generalUtils";

import {
  SlotContainer,
  SlotImageWrapper,
  SlotCopyWrapper,
  SlotMore,
  SlotCopyContent,
  SlotTitleAndOptinTagContainer,
  SlotDescription,
  SlotLink,
  TextContent,
  TagContainer
} from "./styled-components";

const { UserContext, PathContext } = Context;

type InternalRoute = {
  to: Location;
  as: typeof Link;
};

type Target = "_self" | "_blank";

type ExternalRoute = {
  href: string;
  as: "a";
  target: Target;
};

type Params = {
  [key: string]: string;
};

export interface Props {
  content: CardSlot;
  isTopPromo: boolean;
  position: number;
  className: string;
}

const Slot: React.FC<Props> = ({
  content = {
    categoryTitle: "",
    title: "",
    description: "",
    pictureAlt: "",
    pictureUrl: "",
    pictureHeight: 0,
    path: {
      url: "",
      cached_url: "",
      linktype: ""
    },
    promoId: 0,
    promoCode: ""
  },
  isTopPromo = false,
  position = 0,
  className = ""
}) => {
  const userContext = useContext(UserContext);
  const { device } = tvgConf();
  const { product } = tvgConf();
  const domain = tvgConf().config(`domain.${device}`);
  const iosApp = product.indexOf("ios") > -1;

  const setInternalPath = (url: string, type: LinkType): InternalRoute => {
    let locationObject: Location;
    const hasWindow = typeof window !== "undefined";
    const path = hasWindow && !isTvg5() ? window.location.pathname : "/";
    const urlObject = hasWindow && new URL(window.location.href);
    const raceValue = urlObject && urlObject.searchParams.get("race");

    if (type === "story") {
      locationObject = {
        key: "",
        pathname: path,
        hash: "#promos",
        search: raceValue ? `?race=${raceValue}&promo=${url}` : `?promo=${url}`,
        state: {
          showBackBtn: true,
          pageOrigin: "promos"
        }
      };
    } else {
      const pathArray = parseHash(url);
      let pathname;
      let hash;

      if (pathArray.length > 1) {
        pathname = pathArray[0];
        hash = `#${pathArray[1]}`;
      } else {
        pathname = pathArray[0];
        hash = "";
      }

      locationObject = {
        pathname,
        hash,
        search: "",
        state: {},
        key: ""
      };
    }

    return {
      to: locationObject,
      as: Link
    };
  };

  const setExternalPath = (url: string): EventTarget | ExternalRoute => {
    const isInternal: boolean =
      url.indexOf(domain) > -1 || /fdbox|fanduel(\.|-)|racing/gi.test(url);
    const target: Target = isInternal ? "_self" : "_blank";

    return {
      href: url,
      as: "a",
      target
    };
  };

  const handleExternalPath = (
    event: React.SyntheticEvent<HTMLAnchorElement>
  ): void => {
    event.preventDefault();

    window.parent.postMessage(
      { type: "open_url", payload: content.path?.url },
      "*"
    );
  };

  const parseHash = (path: string): Array<string> => path.split("#");
  const dispatch = useDispatch();

  const parseParams = (params: string): Params =>
    params
      .split("&")
      .map((p) => p.split("="))
      .reduce((obj, pair) => {
        const [key, value] = pair.map(decodeURIComponent);
        return { ...obj, [key]: value };
      }, {});

  const handleNavigationDataLayerUpdate = (): void => {
    const isIntPath = content.path?.linktype === "story";
    const promoType = isTopPromo ? "Top Promos" : "All Promos";
    let url;
    let label;
    let params;

    if (isIntPath) {
      url = content.path?.cached_url;
      label = get(content, "content.promoId", "");
    } else {
      url = content?.path?.url.split("?")[1];
      params = !url ? undefined : parseParams(url);
      label = !params ? undefined : params.promoCode;
    }

    dispatch(onbackPromosModal(true));

    mediator.base.dispatch({
      type: "PROMOS_SLOT_PROMO_CLICK",
      payload: {
        gaEventLabel: label,
        module: promoType,
        tag: position,
        destinationUrl: url
      }
    });
  };

  //  @ts-ignore
  const { optedInPromos } = userContext;
  const isOpted: boolean =
    get(optedInPromos, "content.promoId", false) ||
    get(content, "isOptedIn", false);
  const isOptedIn: boolean =
    content.promoId !== undefined && optedInPromos?.[content.promoId];
  const linkType = content?.path?.linktype;
  const urlPath =
    linkType === "story" ? content?.path?.cached_url : content?.path?.url;
  const componentProps = {
    ...(linkType === "story" || (urlPath && urlPath.indexOf("http") < 0)
      ? setInternalPath(urlPath as string, linkType as LinkType)
      : setExternalPath(urlPath as string)),
    "data-qa-label": "promo-slot-link"
  };

  return (
    <SlotContainer data-qa-label="promosSlotContainer" className={className}>
      <PathContext.Consumer>
        {
          // @ts-ignore
          ({ setContent }) => (
            <SlotLink
              {...componentProps}
              // @ts-ignore
              onClick={(event: React.SyntheticEvent<HTMLAnchorElement>) => {
                if (linkType === "story") {
                  setContent(urlPath);
                }

                if (
                  urlPath &&
                  linkType === "url" &&
                  urlPath.indexOf(domain) < 0 &&
                  !/fdbox|fanduel(\.|-)|racing/gi.test(urlPath) &&
                  iosApp
                ) {
                  handleExternalPath(event);
                }

                handleNavigationDataLayerUpdate();
              }}
            >
              <SlotImageWrapper
                imageUrl={content.pictureUrl}
                role="img"
                aria-label={content.pictureAlt}
              />
              <SlotCopyWrapper data-qa-label="slot-wrapper">
                <SlotCopyContent slotImageHeight={content.pictureHeight}>
                  <SlotTitleAndOptinTagContainer>
                    <Paragraph
                      qaLabel="slot-copy-title"
                      fontFamily="condensedRegular"
                      fontSize="12px"
                      textTransform="uppercase"
                      color="--fd-colors-content-subtle"
                    >
                      {content.categoryTitle as string}
                    </Paragraph>
                    {(isOpted || isOptedIn) && (
                      <TagContainer>
                        <Tag
                          size="medium"
                          variant="positive"
                          label="OPTED-IN"
                          qaLabel="slot-promo-card-label"
                        />
                      </TagContainer>
                    )}
                  </SlotTitleAndOptinTagContainer>
                  <TextContent>
                    <Paragraph
                      qaLabel="slot-copy-title"
                      fontFamily="bold"
                      fontSize="16px"
                      lineHeight="125%"
                      paddingBottom={4}
                      color="--fd-colors-content-strong"
                    >
                      {content.title as string}
                    </Paragraph>
                  </TextContent>
                  <SlotDescription
                    qaLabel="slot-copy-title"
                    fontSize="14px"
                    lineHeight="125%"
                    color="--fd-colors-content-default"
                  >
                    <>{Parser(content.description as string)}</>
                  </SlotDescription>
                </SlotCopyContent>
                <SlotMore>
                  <Paragraph
                    qaLabel="slot-more-text"
                    fontFamily="bold"
                    fontSize="14px"
                    color="--fd-colors-component-button-button-link-content-base"
                  >
                    Learn More
                  </Paragraph>
                  <Icon
                    qaLabel="slot-more-icon"
                    lineColor="--fd-colors-component-button-button-link-content-base"
                    name="chevronRight"
                    size="s"
                  />
                </SlotMore>
              </SlotCopyWrapper>
            </SlotLink>
          )
        }
      </PathContext.Consumer>
    </SlotContainer>
  );
};

export default Slot;
